import { Device, type Call } from "@twilio/voice-sdk";
import { telephonyApi } from "../api/telephony";

/**
 * Thin wrapper around `@twilio/voice-sdk` for the operator softphone.
 *
 * Handles token refresh, exposes a Promise-based connect, and
 * lifecycle events as a small callback set. Designed for a single
 * Device instance per page; the consumer is responsible for calling
 * `destroy()` on unmount.
 */
export interface SoftphoneCallbacks {
  onState?(state: SoftphoneState): void;
  onError?(error: Error): void;
}

export type SoftphoneState =
  | { kind: "idle" }
  | { kind: "registering" }
  | { kind: "ready"; identity: string }
  | { kind: "connecting"; to: string }
  | { kind: "in_call"; to: string }
  | { kind: "ended" };

export interface SoftphoneStartOptions {
  rooftopId?: string;
  /** Mint against one explicit Telephony Account (the Telephony
   * page's test panel) instead of the rooftop-driven default. */
  telephonyAccountId?: string;
}

export class Softphone {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private state: SoftphoneState = { kind: "idle" };
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Server-resolved caller-ID for the minted token's account — the
   * default `From` when `dial` isn't given one explicitly. */
  private callerPhoneNumberE164: string | null = null;

  constructor(private readonly callbacks: SoftphoneCallbacks = {}) {}

  async start(opts: SoftphoneStartOptions = {}): Promise<void> {
    this.setState({ kind: "registering" });
    const { token, identity, expiresAt, callerPhoneNumberE164 } =
      await telephonyApi.mintAccessToken(opts);
    this.callerPhoneNumberE164 = callerPhoneNumberE164;
    this.device = new Device(token, { logLevel: 1 });
    this.device.on("registered", () => this.setState({ kind: "ready", identity }));
    this.device.on("error", (err: Error) => {
      this.callbacks.onError?.(err);
    });
    this.device.on("tokenWillExpire", () => {
      void this.refreshToken(opts);
    });
    await this.device.register();
    this.scheduleRefresh(expiresAt, opts);
  }

  async dial(args: {
    to: string;
    from?: string;
    /**
     * Custom params forwarded verbatim by Twilio onto the outbound
     * TwiML App webhook + every CallStatus callback. The TwiML App
     * outbound handler reads `ActivityId` here and stamps the Twilio
     * CallSid back onto the Lumika activity row so the call appears
     * on the contact's timeline / inbox with status + duration.
     */
    extraParams?: Record<string, string>;
  }): Promise<void> {
    if (!this.device) throw new Error("Softphone is not started.");
    this.setState({ kind: "connecting", to: args.to });
    const params: Record<string, string> = { To: args.to, ...(args.extraParams ?? {}) };
    // Explicit From wins; otherwise the server-resolved caller-ID for
    // this rooftop/account. Without one, the TwiML handler's account-
    // level fallback could pick another dealership's number on a
    // shared Telephony Account.
    const from = args.from ?? this.callerPhoneNumberE164;
    if (from) params.From = from;
    const call = await this.device.connect({ params });
    this.activeCall = call;
    call.on("accept", () => this.setState({ kind: "in_call", to: args.to }));
    call.on("disconnect", () => {
      this.activeCall = null;
      this.setState({ kind: "ended" });
    });
    call.on("cancel", () => {
      this.activeCall = null;
      this.setState({ kind: "ended" });
    });
    call.on("reject", () => {
      this.activeCall = null;
      this.setState({ kind: "ended" });
    });
  }

  hangup(): void {
    if (this.activeCall) this.activeCall.disconnect();
  }

  async destroy(): Promise<void> {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (this.activeCall) this.activeCall.disconnect();
    if (this.device) {
      await this.device.unregister();
      this.device.destroy();
    }
    this.device = null;
    this.setState({ kind: "idle" });
  }

  getState(): SoftphoneState {
    return this.state;
  }

  private setState(next: SoftphoneState): void {
    this.state = next;
    this.callbacks.onState?.(next);
  }

  private scheduleRefresh(expiresAt: string, opts: SoftphoneStartOptions): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const ms = Math.max(0, new Date(expiresAt).getTime() - Date.now() - 60_000);
    this.refreshTimer = setTimeout(() => {
      void this.refreshToken(opts);
    }, ms);
  }

  private async refreshToken(opts: SoftphoneStartOptions): Promise<void> {
    if (!this.device) return;
    const { token, expiresAt, callerPhoneNumberE164 } =
      await telephonyApi.mintAccessToken(opts);
    this.callerPhoneNumberE164 = callerPhoneNumberE164;
    await this.device.updateToken(token);
    this.scheduleRefresh(expiresAt, opts);
  }
}
